if (!customElements.get("product-modal-gallery")) {
  // Skala powiekszenia. 2.5x wystarcza, zeby zobaczyc splot tkaniny i szwy,
  // a nie gubi jeszcze kontekstu calego mebla.
  const ZOOM_SCALE = 2.5;
  // Ponizej tego dystansu ruch palca to wciaz klikniecie, a nie przeciaganie.
  const TAP_SLOP = 8;
  // Gest w dol zamyka okno dopiero po takim dystansie - krotszy ruch to
  // najczesciej drgniecie reki przy dotknieciu zdjecia.
  const CLOSE_DISTANCE = 96;
  // Gdy powiekszony kadr dojedzie do krawedzi, taki naddatek ruchu przerzuca
  // na sasiednie zdjecie.
  const EDGE_FLIP = 60;

  class ProductModalGallery extends HTMLElement {
    constructor() {
      super();
      this.swiper = undefined;
      this.content = this.querySelector("[data-modal-gallery-content]");
      this.contentLoaded = false;
      this.loadingPromise = null;
      this.isFullImage = false;
      this.zoomPan = { x: 0, y: 0 };
      this.pointer = null;
      this.suppressClick = false;
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.listen();
      this._variantChangeHandler = (event) => {
        if (!this.content) return;
        this.content.dataset.lazyUrl = `/products/${event.detail.product}?variant=${event.detail.variant}&view=modal-gallery`;
        this.resetContent();
        if (this.style.display === "block") {
          this.loadContent().then(() => {
            this.swiper && this.swiper.update();
            this.playActiveVideo();
          });
        }
      };
      window.addEventListener("variantChange", this._variantChangeHandler);
    }

    resetContent() {
      this.setFullImage(false);
      this.pauseVideos();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = undefined;
      }
      this.contentLoaded = false;
      this.loadingPromise = null;
      if (this.content) {
        this.content.innerHTML =
          '<span class="loader loader--black loader--centered"></span>';
      }
    }

    loadContent() {
      if (this.contentLoaded) return Promise.resolve();
      if (this.loadingPromise) return this.loadingPromise;
      if (!this.content?.dataset.lazyUrl) return Promise.resolve();

      this.loadingPromise = fetch(this.content.dataset.lazyUrl)
        .then((response) => response.text())
        .then((html) => {
          this.content.innerHTML = html;
          this.contentLoaded = true;
          this.loadingPromise = null;
          this.initSwiper();
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
            this.swiper && this.swiper.update();
          }, 100);
        })
        .catch(() => {
          this.loadingPromise = null;
        });

      return this.loadingPromise;
    }

    initSwiper() {
      if (this.swiper || !this.querySelector(".swiper-container")) return;
      this.swiper = new Swiper(this.querySelector(".swiper-container"), {
        // Przewijanie touchpadem: dwa palce w poziomie ruszaja karuzela.
        // forceToAxis pilnuje, zeby ruch w pionie nadal przewijal strone,
        // releaseOnEdges oddaje gest stronie na koncu karuzeli.
        mousewheel: { enabled: true, forceToAxis: true, releaseOnEdges: true, thresholdDelta: 8 },
        loop: false,
        grabCursor: false,
        // Przy prefers-reduced-motion kadr ma sie podmieniac bez przesuwania.
        speed: this.reduceMotion.matches ? 0 : 500,
        spaceBetween: 8,
        slidesPerView: 1,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        on: {
          slideChangeTransitionEnd: () => {
            this.playActiveVideo();
          },
          slideChange: () => {
            // Powiekszenie dotyczy konkretnego kadru - po zmianie zdjecia
            // wracamy do widoku calosci, inaczej kupujacy dostaje nowy obraz
            // od razu przyciety w losowym miejscu.
            this.resetZoom();
            this.syncThumbs(this.swiper ? this.swiper.activeIndex : 0);
          },
        },
      });

      this.bindThumbs();
    }

    // Pas podgladow pod karuzela: klikniecie przenosi na kadr, a aktywna
    // miniatura jest podswietlona i doscrollowana do widoku.
    bindThumbs() {
      const pas = this.querySelector("[data-modal-gallery-thumbs]");
      if (!pas || pas.dataset.bound) return;
      pas.dataset.bound = "1";
      pas.addEventListener("click", (event) => {
        const t = event.target.closest("[data-modal-gallery-thumb]");
        if (!t || !this.swiper) return;
        this.swiper.slideTo(Number(t.dataset.modalGalleryThumb));
      });
    }

    syncThumbs(index) {
      const licznik = this.querySelector("[data-modal-gallery-current]");
      if (licznik) licznik.textContent = String((Number(index) || 0) + 1);

      const miniatury = this.querySelectorAll("[data-modal-gallery-thumb]");
      miniatury.forEach((t, i) => t.classList.toggle("is-active", i === index));
      const aktywna = miniatury[index];
      const pas = this.querySelector("[data-modal-gallery-thumbs]");
      if (!aktywna || !pas) return;
      const off =
        aktywna.offsetLeft - pas.clientWidth / 2 + aktywna.clientWidth / 2;
      pas.scrollTo({
        left: Math.max(0, off),
        behavior: this.reduceMotion.matches ? "auto" : "smooth",
      });
    }

    hydrateVideo(video, shouldPlay = false) {
      if (!video) return;
      if (video.dataset.videoLoaded === "true") {
        if (shouldPlay) {
          video.play().catch(() => {});
        }
        return;
      }
      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.videoLoaded = "true";
      video.load();
      if (shouldPlay) {
        video.play().catch(() => {});
      }
    }

    pauseVideos() {
      this.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    }

    playActiveVideo() {
      this.pauseVideos();
      const video = this.querySelector(".swiper-slide-active video");
      if (video) {
        this.hydrateVideo(video, true);
      }
    }

    activeImage() {
      return this.querySelector(
        ".swiper-slide-active [data-modal-gallery-full-image] img",
      );
    }

    // Ile pikseli w kazda strone wolno przesunac powiekszony kadr. Liczymy z
    // naturalnych wymiarow zdjecia, bo w powiekszeniu obraz jest wpisany w
    // ramke przez object-fit: contain, a nie wypelnia jej w calosci.
    panBounds() {
      const img = this.activeImage();
      const ramkaEl = img && img.parentElement;
      if (!img || !ramkaEl) return { x: 0, y: 0 };
      const ramka = ramkaEl.getBoundingClientRect();
      if (!ramka.width || !ramka.height) return { x: 0, y: 0 };
      const nw = img.naturalWidth || ramka.width;
      const nh = img.naturalHeight || ramka.height;
      const fit = Math.min(ramka.width / nw, ramka.height / nh);
      const w = nw * fit * ZOOM_SCALE;
      const h = nh * fit * ZOOM_SCALE;
      return {
        x: Math.max(0, (w - ramka.width) / 2),
        y: Math.max(0, (h - ramka.height) / 2),
      };
    }

    clampPan(x, y) {
      const b = this.panBounds();
      return {
        x: Math.max(-b.x, Math.min(b.x, x)),
        y: Math.max(-b.y, Math.min(b.y, y)),
      };
    }

    applyZoomTransform() {
      const aktywne = this.activeImage();
      this.querySelectorAll("[data-modal-gallery-full-image] img").forEach(
        (img) => {
          if (!this.isFullImage || img !== aktywne) {
            img.style.transform = "";
            return;
          }
          img.style.transform = `translate3d(${this.zoomPan.x}px, ${this.zoomPan.y}px, 0) scale(${ZOOM_SCALE})`;
        },
      );
    }

    resetZoom() {
      if (!this.isFullImage) return;
      this.isFullImage = false;
      this.classList.remove("product__modal-gallery--full-image");
      this.classList.remove("is-panning");
      this.zoomPan = { x: 0, y: 0 };
      this.applyZoomTransform();
      // Gest karuzeli byl wylaczony na czas przesuwania kadru - wraca razem
      // z widokiem calosci.
      if (this.swiper) this.swiper.allowTouchMove = true;
    }

    setFullImage(active, originX, originY) {
      if (!active) {
        this.resetZoom();
        return;
      }

      this.isFullImage = true;
      this.classList.add("product__modal-gallery--full-image");

      // Punkt dotkniety przez kupujacego ma zostac w tym samym miejscu ekranu,
      // a nie uciec poza kadr - stad przesuniecie proporcjonalne do odleglosci
      // od srodka okna.
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const px = typeof originX === "number" ? originX : cx;
      const py = typeof originY === "number" ? originY : cy;
      this.zoomPan = this.clampPan(
        (px - cx) * (1 - ZOOM_SCALE),
        (py - cy) * (1 - ZOOM_SCALE),
      );

      // Wczesniej powiekszenie blokowalo takze allowSlideNext i allowSlidePrev,
      // wiec strzalki, miniatury i klawiatura przestawaly cokolwiek robic.
      // Blokujemy wylacznie gest karuzeli - palec ma wtedy przesuwac kadr.
      if (this.swiper) this.swiper.allowTouchMove = false;

      this.applyZoomTransform();
    }

    onFullImageClick(event) {
      const trigger = event.target.closest("[data-modal-gallery-full-image]");
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      // Klik, ktory jest tylko koncowka przeciagania kadru, nie ma przelaczac
      // powiekszenia - inaczej kazde przesuniecie zdjecia je wylaczalo.
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }

      this.setFullImage(!this.isFullImage, event.clientX, event.clientY);
    }

    // Podglad gestu zamykajacego: okno jedzie za palcem i blednie, wiec widac,
    // ze ruch w dol cos robi. Przy prefers-reduced-motion zostaje samo blakniecie.
    setCloseDrag(dy) {
      if (!this.content) return;
      if (!dy) {
        this.content.style.transition = "";
        this.content.style.transform = "";
        this.content.style.opacity = "";
        return;
      }
      const postep = Math.min(1, dy / (CLOSE_DISTANCE * 2));
      this.content.style.transition = "none";
      this.content.style.opacity = String(1 - postep * 0.5);
      if (!this.reduceMotion.matches) {
        this.content.style.transform = `translate3d(0, ${dy * 0.6}px, 0)`;
      }
    }

    onPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      this.pointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: this.zoomPan.x,
        panY: this.zoomPan.y,
        moved: false,
        flipped: false,
        naZdjeciu: !!event.target.closest("[data-modal-gallery-full-image]"),
        // Gest zamykajacy jest tylko dla dotyku - na desktopie przeciagniecie
        // mysza w dol to zwykle probe zaznaczenia, a nie chec wyjscia.
        dotyk: event.pointerType !== "mouse",
      };
    }

    onPointerMove(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      const dx = event.clientX - p.startX;
      const dy = event.clientY - p.startY;
      if (!p.moved && Math.sqrt(dx * dx + dy * dy) > TAP_SLOP) {
        p.moved = true;
        if (this.isFullImage && p.naZdjeciu) this.classList.add("is-panning");
      }
      if (!p.moved) return;

      if (this.isFullImage && p.naZdjeciu) {
        const b = this.panBounds();
        const cel = this.clampPan(p.panX + dx, p.panY + dy);
        // Kadr stoi juz na krawedzi, a palec ciagnie dalej w bok - to prosba
        // o nastepne zdjecie, a nie o przesuniecie powiekszenia. Bez tego
        // powiekszony widok byl slepa uliczka.
        const naKrawedzi = Math.abs(cel.x) >= b.x - 0.5;
        const naddatek = p.panX + dx - cel.x;
        if (
          !p.flipped &&
          naKrawedzi &&
          Math.abs(naddatek) > EDGE_FLIP &&
          this.swiper
        ) {
          p.flipped = true;
          if (naddatek < 0) {
            this.swiper.slideNext();
          } else {
            this.swiper.slidePrev();
          }
          return;
        }
        this.zoomPan = cel;
        this.applyZoomTransform();
        return;
      }

      if (
        !this.isFullImage &&
        p.dotyk &&
        dy > 0 &&
        Math.abs(dy) > Math.abs(dx) * 1.5
      ) {
        this.setCloseDrag(dy);
      }
    }

    onPointerUp(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      this.pointer = null;
      this.classList.remove("is-panning");
      this.suppressClick = p.moved && p.naZdjeciu && this.isFullImage;

      const dx = event.clientX - p.startX;
      const dy = event.clientY - p.startY;
      const zamykajacy =
        !this.isFullImage &&
        p.dotyk &&
        p.moved &&
        dy > CLOSE_DISTANCE &&
        Math.abs(dy) > Math.abs(dx) * 1.5;

      this.setCloseDrag(0);
      if (zamykajacy) this.close();
    }

    onKeyDown(event) {
      if (this.style.display !== "block") return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.swiper) return;
      if (event.key === "ArrowRight") this.swiper.slideNext();
      if (event.key === "ArrowLeft") this.swiper.slidePrev();
    }

    listen() {
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler);
      }

      this.querySelector(".product__modal-gallery__close")?.addEventListener(
        "click",
        () => {
          this.close();
        },
      );

      this._fullImageClickHandler = (event) => this.onFullImageClick(event);
      this.addEventListener("click", this._fullImageClickHandler);

      // Gest przesuwania kadru i gest zamykajacy. Listenery sa pasywne i nie
      // wolaja preventDefault, wiec Swiper dalej dostaje swoje zdarzenia.
      this._pointerDownHandler = (event) => this.onPointerDown(event);
      this._pointerMoveHandler = (event) => this.onPointerMove(event);
      this._pointerUpHandler = (event) => this.onPointerUp(event);
      this.addEventListener("pointerdown", this._pointerDownHandler, {
        passive: true,
      });
      // Ruch i puszczenie palca lapiemy na oknie, bo palec potrafi wyjechac
      // poza kadr i wtedy zdarzenie nie doszloby do galerii.
      window.addEventListener("pointermove", this._pointerMoveHandler, {
        passive: true,
      });
      window.addEventListener("pointerup", this._pointerUpHandler, {
        passive: true,
      });
      window.addEventListener("pointercancel", this._pointerUpHandler, {
        passive: true,
      });

      this._keyDownHandler = (event) => this.onKeyDown(event);
      document.addEventListener("keydown", this._keyDownHandler);

      this._docClickHandler = (event) => {
        const slide = event.target.closest(".product__gallery .swiper-slide");
        if (!slide) return;
        this.open(slide.dataset.index);
      };
      document.addEventListener("click", this._docClickHandler);
    }

    open(index = 0) {
      this.setFullImage(false);
      this.style.display = "block";
      document.body.style.overflow = "hidden";
      this.loadContent().then(() => {
        if (this.swiper) {
          this.swiper.slideTo(index, 0);
          this.swiper.update();
          this.syncThumbs(Number(index) || 0);
        }
        this.playActiveVideo();
        // Fokus na zamknieciu: klawiatura ma od razu punkt zaczepienia, a
        // czytnik ekranu oglasza, gdzie kupujacy wladowal.
        this.querySelector(".product__modal-gallery__close")?.focus();
      });
    }

    close() {
      this.pauseVideos();
      this.setFullImage(false);
      this.setCloseDrag(0);
      this.pointer = null;
      this.style.display = "none";
      document.body.style.overflow = "";
    }

    disconnectedCallback() {
      if (this._variantChangeHandler) {
        window.removeEventListener("variantChange", this._variantChangeHandler);
      }
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler);
      }
      if (this._fullImageClickHandler) {
        this.removeEventListener("click", this._fullImageClickHandler);
      }
      if (this._pointerDownHandler) {
        this.removeEventListener("pointerdown", this._pointerDownHandler);
      }
      if (this._pointerMoveHandler) {
        window.removeEventListener("pointermove", this._pointerMoveHandler);
      }
      if (this._pointerUpHandler) {
        window.removeEventListener("pointerup", this._pointerUpHandler);
        window.removeEventListener("pointercancel", this._pointerUpHandler);
      }
      if (this._keyDownHandler) {
        document.removeEventListener("keydown", this._keyDownHandler);
      }
    }
  }
  customElements.define("product-modal-gallery", ProductModalGallery);
}
